"use client";

import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  LineChart,
  type LineRow,
} from "@/components/tool-kit/charts/line-chart";
import {
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useMoney } from "@/components/tools/finance/shared";
import type { StudentLoanStrings } from "@/components/tools/student-loan-repayment-calculator";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import {
  type Loan,
  type Plan,
  type PlanKind,
  paymentSchedule,
  type Simulation,
  type Strategy,
  simulate,
} from "@/lib/tool-kit/finance/student-loans";

const KINDS: readonly PlanKind[] = ["standard", "extended", "graduated"];

interface Format {
  money: (v: number) => string;
  duration: (months: number) => string;
}

function useFormat(currency: string, strings: StudentLoanStrings): Format {
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const money = useMoney(currency);
  const duration = (months: number) => {
    const years = Math.floor(months / 12);
    const rest = months % 12;
    const y = tPlural(strings.yearsUnit, locale, years, { count: fmt(years) });
    const m = tPlural(strings.monthsUnit, locale, rest, { count: fmt(rest) });
    if (years === 0) {
      return m;
    }
    if (rest === 0) {
      return y;
    }
    return t(strings.durationJoin, { years: y, months: m });
  };
  return { money, duration };
}

function planLabel(kind: PlanKind, strings: StudentLoanStrings): string {
  const labels: Record<PlanKind, string> = {
    standard: strings.planStandard,
    extended: strings.planExtended,
    graduated: strings.planGraduated,
  };
  return labels[kind];
}

function PlanComparison({
  runs,
  format,
  strings,
}: {
  runs: Record<PlanKind, Simulation>;
  format: Format;
  strings: StudentLoanStrings;
}) {
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.planTableHeading}</h3>
      <DataTable
        head={[
          strings.scheduleColumn,
          strings.firstColumn,
          strings.peakColumn,
          strings.timeColumn,
          strings.interestColumn,
          strings.paidColumn,
        ]}
        rows={KINDS.map((kind) => [
          planLabel(kind, strings),
          format.money(runs[kind].firstPayment),
          format.money(runs[kind].peakPayment),
          format.duration(runs[kind].months),
          format.money(runs[kind].totalInterest),
          format.money(runs[kind].totalPaid),
        ])}
      />
    </>
  );
}

function BalanceChart({
  scheduled,
  withExtra,
  strings,
  format,
}: {
  scheduled: Simulation;
  withExtra: Simulation | null;
  strings: StudentLoanStrings;
  format: Format;
}) {
  const fmt = useToolNumber();
  const series = [scheduled, ...(withExtra ? [withExtra] : [])];
  const length = Math.max(...series.map((run) => run.yearlyBalance.length));
  const years = (run: Simulation, k: number) =>
    k === run.yearlyBalance.length - 1 && run.months % 12 !== 0
      ? run.months / 12
      : k;
  const longest = series.find((run) => run.yearlyBalance.length === length);
  const rows: LineRow[] = Array.from({ length }, (_, k) => {
    const x = longest ? years(longest, k) : k;
    return {
      label: fmt(x, { maximumFractionDigits: 2 }),
      x,
      values: series.map((run) => run.yearlyBalance[k] ?? 0),
    };
  });
  const names = withExtra
    ? [strings.seriesScheduled, strings.seriesExtra]
    : [strings.seriesScheduled];
  const style = {
    title: strings.chartTitle,
    xTitle: strings.chartXTitle,
    yTitle: strings.chartYTitle,
    color: "blue" as const,
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  };
  return (
    <ChartFigure
      apa={false}
      data={toTsv([
        [strings.chartXTitle, ...names],
        ...rows.map((row) => [row.label, ...row.values]),
      ])}
      figureNumber="1"
      filename={chartFilename(strings.chartTitle, "student-loan-balance")}
      labels={strings}
      note=""
      table={null}
      title={strings.chartTitle}
    >
      <LineChart
        description={t(strings.chartDescription, {
          start: format.money(scheduled.yearlyBalance[0]),
        })}
        fmt={fmt}
        id="sl-chart"
        label={strings.chartLabel}
        markers={rows.length <= 12}
        names={names}
        numeric
        rows={rows}
        style={style}
        zero
      />
    </ChartFigure>
  );
}

export function StudentLoanResults({
  loans,
  plans,
  kind,
  extra,
  strategy,
  currency,
  digits,
  strings,
}: {
  loans: Loan[];
  plans: Record<PlanKind, Plan>;
  kind: PlanKind;
  extra: number;
  strategy: Exclude<Strategy, "none">;
  currency: string;
  digits: number;
  strings: StudentLoanStrings;
}) {
  const fmt = useToolNumber();
  const format = useFormat(currency, strings);
  const run = (plan: Plan, how: Strategy) =>
    simulate({ loans, plan, extra, strategy: how, digits });
  const none = { plan: plans[kind] };
  const runs = {
    standard: run(plans.standard, "none"),
    extended: run(plans.extended, "none"),
    graduated: run(plans.graduated, "none"),
  };
  const scheduled = runs[kind];
  const chosen = run(none.plan, strategy);
  const other = run(
    none.plan,
    strategy === "avalanche" ? "snowball" : "avalanche"
  );
  const avalanche = strategy === "avalanche" ? chosen : other;
  const snowball = strategy === "avalanche" ? other : chosen;
  const hasExtra = extra > 0;
  const incomplete =
    Object.values(runs).some((r) => r.incomplete) || chosen.incomplete;
  const lowFirst =
    kind === "graduated" &&
    loans.some(
      (loan) =>
        (paymentSchedule(loan, plans.graduated, digits)[0] ?? 0) <
        (loan.balance * loan.rate) / 12
    );
  if (incomplete) {
    return <Notice>{strings.incomplete}</Notice>;
  }
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      <PlanComparison format={format} runs={runs} strings={strings} />
      {lowFirst ? <Notice>{strings.graduatedLow}</Notice> : null}
      <StatGrid>
        <StatCard
          emphasis
          label={strings.timeToRepay}
          value={format.duration((hasExtra ? chosen : scheduled).months)}
        />
        <StatCard
          label={strings.totalInterestStat}
          value={format.money((hasExtra ? chosen : scheduled).totalInterest)}
        />
        <StatCard
          label={strings.totalPaidStat}
          value={format.money((hasExtra ? chosen : scheduled).totalPaid)}
        />
        {hasExtra ? (
          <StatCard
            label={strings.interestSaved}
            value={format.money(scheduled.totalInterest - chosen.totalInterest)}
          />
        ) : null}
        {hasExtra ? (
          <StatCard
            label={strings.timeSaved}
            value={format.duration(scheduled.months - chosen.months)}
          />
        ) : null}
      </StatGrid>
      <BalanceChart
        format={format}
        scheduled={scheduled}
        strings={strings}
        withExtra={hasExtra ? chosen : null}
      />
      <h3 className={WIDGET_H3}>{strings.loanTableHeading}</h3>
      <DataTable
        head={[
          strings.loanColumn,
          strings.balanceColumn,
          strings.rateColumn,
          strings.payoffColumn,
          strings.loanInterestColumn,
        ]}
        rows={loans.map((loan, index) => [
          loan.name,
          format.money(loan.balance),
          `${fmt(loan.rate * 100, { maximumFractionDigits: 3 })}%`,
          fmt((hasExtra ? chosen : scheduled).perLoan[index].payoffMonth ?? 0),
          format.money((hasExtra ? chosen : scheduled).perLoan[index].interest),
        ])}
      />
      <h3 className={WIDGET_H3}>{strings.strategyTableHeading}</h3>
      {hasExtra ? (
        <DataTable
          head={[
            strings.strategyColumn,
            strings.timeColumn,
            strings.interestColumn,
            strings.paidColumn,
          ]}
          rows={(
            [
              { label: strings.strategyNone, sim: scheduled },
              { label: strings.strategyAvalanche, sim: avalanche },
              { label: strings.strategySnowball, sim: snowball },
            ] as const
          ).map(({ label, sim }) => [
            label,
            format.duration(sim.months),
            format.money(sim.totalInterest),
            format.money(sim.totalPaid),
          ])}
        />
      ) : (
        <Notice>{strings.needExtra}</Notice>
      )}
    </section>
  );
}
